import {
    ADS_STATES,
    CONNECTIVITY_STATES,
    DEVIATION_SEVERITIES,
    DEVIATION_STATES,
    DRIVER_MODES,
    DRIVING_STATES,
    hasBlockingDeviation,
    isDeviationActive,
    MRM_EXECUTION_PHASES,
    MRM_STATES,
    MRM_TRIGGERS,
    MRM_TYPES,
} from 'domain/mappers/vehicleFromRestV2';
import type { FieldError, MonitoredField } from 'domain/monitored';
import type {
    AdsStateFE,
    ConnectivityDetailsFE,
    DeviationFE,
    DriverModeFE,
    DrivingStateFE,
    GeoPositionFE,
    MrmDetailsFE,
    VehicleFE,
} from 'domain/vehicle';
import { toDate } from 'helper/time';

import type {
    AdsStateFieldV2,
    ConnectivityStatusFieldV2,
    DeviationsFieldV2,
    DriverModeFieldV2,
    DrivingStateFieldV2,
    ErrorV2,
    MrmStateFieldV2,
    PositionFieldV2,
    SpeedMsFieldV2,
    VehicleV2,
} from 'API';

// Local on purpose — do NOT replace with `restMapping.toFieldError`.
const toFieldError = (error?: ErrorV2 | null): FieldError | null => {
    if (error == null) {
        return null;
    }
    return { type: error.type, updatedAt: toDate(error.raisedAt) };
};

const connectivityFromV2 = (field?: ConnectivityStatusFieldV2 | null): MonitoredField<ConnectivityDetailsFE> => {
    if (field == null) {
        return {};
    }
    const ttl = field.ttl ?? null;
    const error = toFieldError(field.error);
    if (field.value == null) {
        return { ttl, value: null, error };
    }
    return {
        ttl,
        value: {
            value: {
                // `ConnectivityState` is unprefixed on the wire, hence the empty prefix. Reading
                // `CONNECTIVITY_STATES` rather than trusting the generated type: an unexpected member
                // (a future `DEGRADED`) would otherwise pass straight through typed as
                // `ConnectivityStateFE`. An absent value still folds to 'UNKNOWN'.
                state: stripEnum(field.value.state, '', CONNECTIVITY_STATES),
                expectDisconnection: field.value.expectDisconnection,
                latency: field.value.latency ?? undefined,
            },
            updatedAt: toDate(field.value.updatedAt),
        },
        error,
    };
};

const positionFromV2 = (field?: PositionFieldV2 | null): MonitoredField<GeoPositionFE> => {
    if (field == null) {
        return {};
    }
    const ttl = field.ttl ?? null;
    const error = toFieldError(field.error);
    const raw = field.value?.position;
    if (field.value == null || raw == null || typeof raw.latitude !== 'number' || typeof raw.longitude !== 'number') {
        return { ttl, value: null, error };
    }
    const position: GeoPositionFE = { lat: raw.latitude, lng: raw.longitude };
    if (typeof raw.heading === 'number') {
        position.heading = raw.heading;
    }
    if (typeof raw.altitude === 'number') {
        position.altitudeMeters = raw.altitude;
    }
    return {
        ttl,
        value: { value: position, updatedAt: toDate(field.value.updatedAt) },
        error,
    };
};

const stripEnum = <T extends string>(raw: unknown, prefix: string, allowed: readonly T[]): T => {
    const member = typeof raw === 'string' && raw.startsWith(prefix) ? raw.slice(prefix.length) : raw;
    return (allowed as readonly string[]).includes(member as string) ? (member as T) : ('UNKNOWN' as T);
};

// The live `VehicleV2.mrmState` carries the GraphQL enums (prefixed: `MRM_STATE_*`,
// `MRM_EXECUTION_PHASE_*`, `MRM_TYPE_*`, `MRM_TRIGGER_*`). Strip the prefix to the FE union,
// reading the member sets `vehicleFromRestV2` reads for the same fields, so both transports fold an
// unrecognised member to the same 'UNKNOWN'.
const mrmFromV2 = (field?: MrmStateFieldV2 | null): MonitoredField<MrmDetailsFE> => {
    if (field == null) {
        return {};
    }
    const ttl = field.ttl ?? null;
    const error = toFieldError(field.error);
    const data = field.value?.data;
    if (field.value == null || data == null) {
        return { ttl, value: null, error };
    }
    return {
        ttl,
        value: {
            value: {
                state: stripEnum(data.value, 'MRM_STATE_', MRM_STATES),
                causes: (data.causes ?? []).filter((c): c is string => c != null),
                executionPhase:
                    data.executionPhase == null
                        ? undefined
                        : stripEnum(data.executionPhase, 'MRM_EXECUTION_PHASE_', MRM_EXECUTION_PHASES),
                mrmType: data.mrmType == null ? undefined : stripEnum(data.mrmType, 'MRM_TYPE_', MRM_TYPES),
                triggerBy: data.triggerBy == null ? undefined : stripEnum(data.triggerBy, 'MRM_TRIGGER_', MRM_TRIGGERS),
                startedAt: data.startedAt ?? undefined,
            },
            updatedAt: toDate(field.value.updatedAt),
        },
        error,
    };
};

const adsFromV2 = (field?: AdsStateFieldV2 | null): MonitoredField<AdsStateFE> => {
    if (field == null) {
        return {};
    }
    const ttl = field.ttl ?? null;
    const error = toFieldError(field.error);
    if (field.value == null) {
        return { ttl, value: null, error };
    }
    return {
        ttl,
        value: { value: stripEnum(field.value.state, '', ADS_STATES), updatedAt: toDate(field.value.updatedAt) },
        error,
    };
};

// `DrivingState` is unprefixed on the wire too, so the same empty prefix applies.
const drivingStateFromV2 = (field?: DrivingStateFieldV2 | null): MonitoredField<DrivingStateFE> => {
    if (field == null) {
        return {};
    }
    const ttl = field.ttl ?? null;
    const error = toFieldError(field.error);
    if (field.value == null) {
        return { ttl, value: null, error };
    }
    return {
        ttl,
        value: { value: stripEnum(field.value.state, '', DRIVING_STATES), updatedAt: toDate(field.value.updatedAt) },
        error,
    };
};

const driverModeFromV2 = (field?: DriverModeFieldV2 | null): MonitoredField<DriverModeFE> => {
    if (field == null) {
        return {};
    }
    const ttl = field.ttl ?? null;
    const error = toFieldError(field.error);
    if (field.value == null) {
        return { ttl, value: null, error };
    }
    return {
        ttl,
        value: {
            value: stripEnum(field.value.mode, 'DRIVER_MODE_', DRIVER_MODES),
            updatedAt: toDate(field.value.updatedAt),
        },
        error,
    };
};

const speedFromV2 = (field?: SpeedMsFieldV2 | null): MonitoredField<number> => {
    if (field == null) {
        return {};
    }
    const ttl = field.ttl ?? null;
    const error = toFieldError(field.error);
    const raw = field.value;
    if (raw == null || typeof raw.speedMs !== 'number') {
        return { ttl, value: null, error };
    }
    return { ttl, value: { value: raw.speedMs * 3.6, updatedAt: toDate(raw.updatedAt) }, error };
};

const deviationFromV2 = (field?: DeviationsFieldV2 | null): MonitoredField<DeviationFE> => {
    if (field == null) {
        return {};
    }
    const ttl = field.ttl ?? null;
    const error = toFieldError(field.error);
    const raw = field.value?.deviation;
    if (field.value == null || raw == null) {
        return { ttl, value: null, error };
    }

    const state = stripEnum(raw.state, '', DEVIATION_STATES);
    const resolvedAt = toDate(raw.resolvedAt);
    return {
        ttl,
        value: {
            value: {
                id: raw.id ?? '',
                description: raw.description ?? '',
                category: raw.category ?? '',
                origin: raw.origin ?? '',
                isBlocking: raw.isBlocking ?? false,
                // `Severity.INVALID` is a real GraphQL member and is absent from
                // DEVIATION_SEVERITIES, so it folds to 'UNKNOWN' — the same value the REST mapper
                // produces for it.
                severity: stripEnum(raw.severity, '', DEVIATION_SEVERITIES),
                state,
                sentAt: toDate(raw.sentAt),
                resolvedAt,
                relatedDeviations: (raw.relatedDeviations ?? []).filter((r): r is string => r != null),
                isActive: isDeviationActive(state, resolvedAt),
            },
            updatedAt: toDate(field.value.updatedAt),
        },
        error,
    };
};

/**
 * Keys the REST payload owns: a subscription snapshot never overwrites them.
 *
 * `keyof Pick<VehicleFE, ...>` rather than a bare string union on purpose — `Pick` constrains each
 * name to a real key, so a typo or a field renamed on `VehicleFE` fails here instead of quietly
 * excluding nothing.
 */
type RestOwnedFieldKey = keyof Pick<
    VehicleFE,
    'vin' | 'name' | 'licensePlate' | 'fleetId' | 'owners' | 'activeMissionId'
>;

type DeviationFieldKey = keyof Pick<VehicleFE, 'deviations' | 'hasBlockingDeviation'>;

type AlwaysPatchedFieldKey = Exclude<keyof VehicleFE, RestOwnedFieldKey | DeviationFieldKey>;

type AlwaysPatchedFields = Pick<VehicleFE, AlwaysPatchedFieldKey>;

export type VehicleLiveFields =
    | (AlwaysPatchedFields & Pick<VehicleFE, DeviationFieldKey>)
    | (AlwaysPatchedFields & { deviations?: undefined; hasBlockingDeviation?: undefined });

export const vehicleLiveFieldsFromV2 = (live: VehicleV2): VehicleLiveFields => {
    const always: AlwaysPatchedFields = {
        ads: adsFromV2(live.adsState),
        connectivity: connectivityFromV2(live.connectivityStatus),
        drivingState: drivingStateFromV2(live.drivingState),
        driverMode: driverModeFromV2(live.driverMode),
        position: positionFromV2(live.position),
        speedKmh: speedFromV2(live.speedMs),
        mrm: mrmFromV2(live.mrmState),
        latestVehicleUpdate: toDate(live.latestVehicleUpdate),
        timestamp: toDate(live.timestamp),
    };

    if (!Array.isArray(live.deviations)) {
        return always;
    }
    const deviations = live.deviations.map(deviationFromV2);
    return { ...always, deviations, hasBlockingDeviation: hasBlockingDeviation(deviations) };
};

export const applyVehicleLiveUpdate = (target: VehicleFE, live: VehicleV2): void => {
    const fields = vehicleLiveFieldsFromV2(live);
    target.ads = fields.ads;
    target.connectivity = fields.connectivity;
    target.drivingState = fields.drivingState;
    target.driverMode = fields.driverMode;
    target.position = fields.position;
    target.speedKmh = fields.speedKmh;
    target.mrm = fields.mrm;
    target.latestVehicleUpdate = fields.latestVehicleUpdate;
    target.timestamp = fields.timestamp;
    if (fields.deviations !== undefined) {
        target.deviations = fields.deviations;
        target.hasBlockingDeviation = fields.hasBlockingDeviation;
    }
};
